/**
 * MATRIX logo mark — a 5x5 pixel-grid "M" glyph rendered as white cells
 * on a solid black tile, with the dead-center cell in electric blue
 * (the brand's "matrix node"). The mark is pure SVG so it scales crisply,
 * and it animates subtly on hover.
 */

// 5×5 grid: 1 = visible (white), 2 = accent (blue), 0 = hole (black bg)
const M_GRID = [
  [1, 0, 0, 0, 1],
  [1, 1, 0, 1, 1],
  [1, 0, 2, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
];

export function LogoMark({ size = 32, className = "" }) {
  // 5 cells × 2 units = 10. Plus 1u padding on all sides = 12.
  const cell = 2;
  const pad = 1;
  const side = pad * 2 + M_GRID.length * cell;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${side} ${side}`}
      className={className}
      aria-hidden="true"
    >
      <rect width={side} height={side} fill="#0A0A0A" />
      {M_GRID.map((row, y) =>
        row.map((v, x) =>
          v === 0 ? null : (
            <rect
              key={`${x}-${y}`}
              x={pad + x * cell}
              y={pad + y * cell}
              width={cell}
              height={cell}
              fill={v === 2 ? "#0033FF" : "#FFFFFF"}
            />
          )
        )
      )}
    </svg>
  );
}

/** Full logo: mark + wordmark. Use inside headers / login / footer. */
export default function Logo({
  size = 34,
  tone = "dark", // "dark" = black text on light bg; "light" = white text on dark bg
  showBracket = true,
  className = "",
}) {
  const text = tone === "light" ? "text-white" : "text-[#0A0A0A]";
  const bracket = tone === "light" ? "text-white/40" : "text-neutral-400";
  return (
    <div className={`flex items-center gap-2 group ${className}`}>
      <div className="transition-transform duration-200 group-hover:rotate-[-6deg]">
        <LogoMark size={size} />
      </div>
      <div className="flex items-baseline">
        {showBracket && (
          <span
            className={`font-display font-black ${bracket} text-[0.9em] leading-none mr-[1px]`}
            style={{ fontSize: size * 0.5 }}
          >
            [
          </span>
        )}
        <span
          className={`font-display font-black ${text} tracking-[-0.04em] leading-none`}
          style={{ fontSize: size * 0.58, letterSpacing: "-0.04em" }}
        >
          MATRIX
        </span>
        {showBracket && (
          <span
            className={`font-display font-black ${bracket} text-[0.9em] leading-none ml-[1px]`}
            style={{ fontSize: size * 0.5 }}
          >
            ]
          </span>
        )}
        <span
          className="font-display font-black text-[#0033FF] leading-none"
          style={{ fontSize: size * 0.58 }}
        >
          .
        </span>
      </div>
    </div>
  );
}
